/* ===========================================================================
   YEMAT — cart drawer upsell fix
   Added 6 Oct 2026.

   Loaded by sections/global-menu.liquid, which layout/theme.liquid renders
   unconditionally on the line directly above the cart-drawer section. That
   gives this file exactly the same page coverage as the drawer itself.

   Fixes three defects verified on both desktop and a real iPhone:
     1. "YOU MAY ALSO LIKE" offered products that were already in the cart
        (adding Force Hoodie Black showed Force Hoodie Black back as an upsell).
     2. Upsell cards showed only the first product tag ("450 GSM") — no
        product name and no price.
     3. The block stayed visible even when every card had been filtered out.

   Deliberately non-destructive: this file only READS the drawer's rendered
   DOM and ADDS elements. It never edits sections/cart-drawer.liquid, so the
   drawer keeps working exactly as before if this file is ever removed.

   Coverage note: which products appear is a THEME SETTING
   (settings.cart_drawer_upsell_products), not code. Edit it in the theme
   editor — no code change needed.
   =========================================================================== */

(function () {
  'use strict';

  var BLOCK_ID = 'cart-upsell-block';
  var ITEMS_ID = 'cart-items';
  var CARD_SEL = '.cart-upsell__card';
  var SWIPER_SEL = '.cart-upsell-swiper';
  var PRODUCT_LINK = 'a[href*="/products/"]';

  var cfg = window.YMT_UPSELL || {};
  var productCache = {};
  var inflight = {};
  var decorated = (typeof WeakSet === 'function') ? new WeakSet() : null;
  var observer = null;
  var syncTimer = null;
  var burstCount = 0;
  var burstReset = 0;

  /* ---------- helpers ---------------------------------------------------- */

  function handleFromHref(href) {
    if (!href) return '';
    var tail = String(href).split('/products/')[1];
    if (!tail) return '';
    return tail.split('?')[0].split('#')[0].split('/')[0].toLowerCase();
  }

  function cardHandle(card) {
    var a = card.querySelector(PRODUCT_LINK);
    return a ? handleFromHref(a.getAttribute('href')) : '';
  }

  function closestSlide(el) {
    if (el && typeof el.closest === 'function') {
      var s = el.closest('.swiper-slide');
      if (s) return s;
    }
    return el ? el.parentNode : null;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Mirrors Shopify's own formatMoney so upsell prices render byte-identically
     to the cart line-item prices above them. Prefers the theme's function when
     it is already on the page; otherwise formats with the same algorithm. */
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (precision == null) precision = 2;
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(cents) || cents == null) return '0';
    var n = (cents / 100.0).toFixed(precision);
    var parts = n.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var rest = parts[1] ? decimal + parts[1] : '';
    return dollars + rest;
  }

  function moneyFormatString() {
    if (cfg.showCurrency && cfg.moneyFormatWithCurrency) return cfg.moneyFormatWithCurrency;
    if (cfg.moneyFormat) return cfg.moneyFormat;
    var s = window.theme && window.theme.settings;
    if (s) {
      if (cfg.showCurrency && s.moneyFormatWithCurrency) return s.moneyFormatWithCurrency;
      if (s.moneyFormat) return s.moneyFormat;
    }
    return '${{amount}}';
  }

  function money(cents) {
    var fmt = moneyFormatString();
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, fmt);
      }
    } catch (e) { /* fall through to the local formatter */ }

    var re = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(re);
    if (!match) return fmt;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.'); break;
      default:
        value = formatWithDelimiters(cents, 2); break;
    }
    return fmt.replace(re, value);
  }

  /* ---------- product data ---------------------------------------------- */

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    if (inflight[handle]) return inflight[handle];

    var p = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        delete inflight[handle];
        if (data && typeof data.price === 'number') productCache[handle] = data;
        return productCache[handle] || null;
      })
      .catch(function () {
        delete inflight[handle];
        return null;
      });

    inflight[handle] = p;
    return p;
  }

  /* ---------- 2. name + price on each card ------------------------------- */

  function buildInfo(product, href) {
    var now = product.price;
    var was = product.compare_at_price_max || product.compare_at_price || 0;

    var nowText = money(now);
    if (product.price_varies) nowText = 'From ' + nowText;

    var priceInner = '';
    if (was && was > now) {
      priceInner += '<s class="ymt-upsell__was">' + money(was) + '</s>';
    }
    priceInner += '<span class="ymt-upsell__now">' + nowText + '</span>';

    var a = document.createElement('a');
    a.className = 'ymt-upsell__info';
    a.setAttribute('href', href || ('/products/' + product.handle));
    a.innerHTML =
      '<span class="ymt-upsell__name">' + esc(product.title) + '</span>' +
      '<span class="ymt-upsell__price">' + priceInner + '</span>';
    return a;
  }

  function decorate() {
    var block = document.getElementById(BLOCK_ID);
    if (!block) return;

    var cards = block.querySelectorAll(CARD_SEL);
    Array.prototype.forEach.call(cards, function (card) {
      if (decorated && decorated.has(card)) return;
      if (card.querySelector('.ymt-upsell__info')) return;

      var link = card.querySelector(PRODUCT_LINK);
      var href = link ? link.getAttribute('href') : '';
      var handle = handleFromHref(href);
      if (!handle) return;

      if (decorated) decorated.add(card);

      fetchProduct(handle).then(function (product) {
        if (!product) {
          /* Data unavailable — leave the card exactly as the theme rendered it
             and allow a later pass to retry rather than showing a blank price. */
          if (decorated) decorated.delete(card);
          return;
        }
        if (card.querySelector('.ymt-upsell__info')) return;
        var info = buildInfo(product, href);
        var wrap = card.querySelector('.cart-upsell__img-wrap');
        if (wrap && wrap.parentNode === card) {
          card.insertBefore(info, wrap.nextSibling);
        } else {
          card.appendChild(info);
        }
        updateSwiper();
      });
    });
  }

  /* ---------- 1 + 3. hide in-cart products, hide empty block ------------- */

  function cartHandles() {
    var out = [];
    var items = document.getElementById(ITEMS_ID);
    if (!items) return out;
    var links = items.querySelectorAll(PRODUCT_LINK);
    Array.prototype.forEach.call(links, function (a) {
      var h = handleFromHref(a.getAttribute('href'));
      if (h && out.indexOf(h) === -1) out.push(h);
    });
    return out;
  }

  /* Writes only when the value actually changes. That idempotency is what
     stops the MutationObserver below from re-triggering itself forever. */
  function setDisplay(el, value) {
    if (!el) return false;
    if (el.style.display === value) return false;
    el.style.display = value;
    return true;
  }

  function updateSwiper() {
    try {
      var el = document.querySelector(SWIPER_SEL);
      if (el && el.swiper && typeof el.swiper.update === 'function') el.swiper.update();
    } catch (e) { /* a stale swiper instance must never break the drawer */ }
  }

  function sync() {
    var block = document.getElementById(BLOCK_ID);
    if (!block) return;

    var cards = block.querySelectorAll(CARD_SEL);
    if (!cards.length) return;

    var inCart = cartHandles();
    var visible = 0;
    var changed = false;

    Array.prototype.forEach.call(cards, function (card) {
      var handle = cardHandle(card);
      var target = closestSlide(card) || card;
      var hide = !!handle && inCart.indexOf(handle) !== -1;
      if (setDisplay(target, hide ? 'none' : '')) changed = true;
      if (!hide) visible++;
    });

    /* Only take over the block's own visibility while the cart has items.
       On an empty cart the theme hides it, and we must not fight that. */
    var items = document.getElementById(ITEMS_ID);
    var cartHasItems = !!(items && items.querySelector('.cart-item'));
    if (cartHasItems) {
      if (setDisplay(block, visible === 0 ? 'none' : '')) changed = true;
    }

    if (changed) updateSwiper();
  }

  function scheduleSync() {
    var now = Date.now();
    if (now - burstReset > 1000) { burstReset = now; burstCount = 0; }
    burstCount++;
    /* Defensive circuit breaker: if something else on the page fights us,
       stop observing rather than spin. The drawer keeps working. */
    if (burstCount > 40) {
      if (observer) { observer.disconnect(); observer = null; }
      return;
    }
    if (syncTimer) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(function () {
      syncTimer = null;
      try { sync(); } catch (e) { /* never block checkout */ }
      try { decorate(); } catch (e) { /* never block checkout */ }
    }, 120);
  }

  /* ---------- boot ------------------------------------------------------- */

  function boot() {
    var block = document.getElementById(BLOCK_ID);
    if (!block) return; /* no upsell block on this page — nothing to do */

    try { decorate(); } catch (e) { /* no-op */ }
    try { sync(); } catch (e) { /* no-op */ }

    if (typeof MutationObserver !== 'function') return;

    observer = new MutationObserver(scheduleSync);

    var items = document.getElementById(ITEMS_ID);
    if (items) observer.observe(items, { childList: true, subtree: true });
    observer.observe(block, { attributes: true, attributeFilter: ['style'] });

    var drawer = document.getElementById('cart-drawer');
    if (drawer) observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();